/**
 * Scheduled Meetings Context
 *
 * Provides scheduled/personal meetings data and operations throughout the app.
 * Uses TanStack Query for automatic retry, caching, and state management.
 *
 * Features:
 * - Lazy loading: meetings are fetched only when needed
 * - Background prefetch: optional automatic fetch after initial page load
 * - Optimistic updates: UI updates immediately while API calls are in flight
 * - Automatic retry: failed requests are retried with exponential backoff
 * - Caching: reduces unnecessary API calls
 *
 * @example
 * ```tsx
 * // Access meetings data
 * const { meetings, isLoading } = useScheduledMeetings();
 *
 * // Create a meeting
 * const { createMeeting } = useScheduledMeetings();
 * await createMeeting({ description: 'My Meeting' });
 *
 * // Lazy load meetings
 * const { getMeetings } = useScheduledMeetings();
 * const meetings = await getMeetings();
 * ```
 */

import { useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { createContext, useMemo } from 'react';
import { useUser } from '../UserContext';
import { DEFAULT_CONFIG } from './helpers';
import useCreateMeetingMutation from './internal_hooks/useCreateMeetingMutation';
import useDeleteMeetingMutation from './internal_hooks/useDeleteMeetingMutation';
import useMeetingActions from './internal_hooks/useMeetingActions';
import useMeetingsListQuery from './internal_hooks/useMeetingsListQuery';
import useScheduledMeetingsApi from './internal_hooks/useScheduledMeetingsApi';
import useUpdateMeetingMutation from './internal_hooks/useUpdateMeetingMutation';
import type {
  ScheduledMeeting,
  ScheduledMeetingsContextValue,
  ScheduledMeetingsProviderProps,
} from './types';

/**
 * Context definition
 */
export const ScheduledMeetingsContext = createContext<ScheduledMeetingsContextValue | null>(null);

/**
 * Scheduled Meetings Provider Component
 */
export const ScheduledMeetingsProvider: React.FC<ScheduledMeetingsProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useUser();
  const api = useScheduledMeetingsApi();

  const options = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      initialQuery: {
        ...DEFAULT_CONFIG.initialQuery,
      },
    }),
    [],
  );

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isSuccess,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    dataUpdatedAt,
  } = useMeetingsListQuery(options);

  const meetings = useMemo(() => data?.pages.flat() ?? [], [data]);

  const lastPageMeetings = useMemo(() => {
    if (!data?.pages?.length) {
      return [] as ScheduledMeeting[];
    }
    const lastPage = data.pages[data.pages.length - 1];
    return lastPage ?? [];
  }, [data]);

  const createMeetingMutation = useCreateMeetingMutation(options);
  const updateMeetingMutation = useUpdateMeetingMutation(options);
  const deleteMeetingMutation = useDeleteMeetingMutation(options);

  const {
    getMeetings,
    getAllMeetingsFlat,
    getMeeting,
    refreshMeetings,
    getMeetingByRoom,
    isMeetingOwned,
    clearError,
    invalidateMeetings,
  } = useMeetingActions({
    queryClient,
    isAuthenticated,
    api,
    options,
    meetings,
    refetch,
  });

  const contextValue = useMemo<ScheduledMeetingsContextValue>(
    () => ({
      // State
      meetings,
      lastPageMeetings,
      isLoading,
      isFetching,
      isFetchingNextPage,
      isInitialized: isSuccess,
      error: error as Error | null,
      totalCount: meetings.length,
      lastFetchedAt: dataUpdatedAt > 0 ? new Date(dataUpdatedAt).toISOString() : null,
      hasNextPage: Boolean(hasNextPage),
      // Actions
      getMeetings,
      getAllMeetingsFlat,
      getMeeting,
      refreshMeetings,
      fetchNextPage: async () => {
        await fetchNextPage();
      },
      createMeeting: createMeetingMutation.mutateAsync,
      updateMeeting: updateMeetingMutation.mutateAsync,
      deleteMeeting: deleteMeetingMutation.mutateAsync,
      getMeetingByRoom,
      isMeetingOwned,
      clearError,
      invalidateMeetings,
      mutations: {
        create: {
          isPending: createMeetingMutation.isPending,
        },
        update: {
          isPending: updateMeetingMutation.isPending,
        },
        delete: {
          isPending: deleteMeetingMutation.isPending,
          pendingRoom: deleteMeetingMutation.variables as string | undefined,
        },
      },
    }),
    [
      meetings,
      lastPageMeetings,
      isLoading,
      isFetching,
      isFetchingNextPage,
      isSuccess,
      error,
      hasNextPage,
      dataUpdatedAt,
      getMeetings,
      getAllMeetingsFlat,
      getMeeting,
      refreshMeetings,
      fetchNextPage,
      createMeetingMutation.mutateAsync,
      updateMeetingMutation.mutateAsync,
      deleteMeetingMutation.mutateAsync,
      getMeetingByRoom,
      isMeetingOwned,
      clearError,
      invalidateMeetings,
      createMeetingMutation.isPending,
      updateMeetingMutation.isPending,
      deleteMeetingMutation.isPending,
      deleteMeetingMutation.variables,
    ],
  );

  return (
    <ScheduledMeetingsContext.Provider value={contextValue}>
      {children}
    </ScheduledMeetingsContext.Provider>
  );
};
